"use client"

import { productPagePath } from "@/lib/parts/fpn"
import { toFeatureFacetOptions } from "@/lib/collections/feature-facet-labels"
import type { CollectionQueryExample, CollectionQueryTopic } from "@/lib/collection-query-answers"
import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { extractCapacityFromTitle } from "@/lib/collection-filters"
import { applyBrandRules } from "@/lib/brand-rules"
import { TrustStrip } from "@/components/trust/trust-strip"
import { keepIndexedCollectionLinks } from "@/lib/chrome-link-gate"
import Link from "next/link"
import { CollectionSiblingLinks } from "./_components/collection-sibling-links"
import { CollectionSidebarBrandkit, type CollectionFilters, type FacetCounts } from "@/components/collection-sidebar-brandkit"
import { TRUE_NATURE_MEATS_AD, isFreezerSurface } from "@/lib/ad-config"
// Imported by file, not through the "@/components/brand-kit" barrel: the barrel
// re-exports the appliance page's compatible-filter card, and through it that
// card, its fitment helper and the filter brand tile rode into this route's
// First Load JS although a collection never renders them (measured 09/19/2026,
// when the bundle budget went red on /collections/[slug]).
import { ProductGridCard } from "@/components/brand-kit/product-grid-card"
import { BrandLogoGrid } from "@/components/brand-kit/brand-logo-grid"
import { Pagination } from "@/components/brand-kit/pagination"
import { Breadcrumb } from "@/components/brand-kit/breadcrumb"
import { FilterPill } from "@/components/brand-kit/filter-pill"
import { SectionHeader } from "@/components/brand-kit/section-header"
import { PageSectionHeader } from "@/components/brand-kit/page-section-header"
import { ConsiderationCard } from "@/components/brand-kit/consideration-card"
import { FAQAccordion } from "@/components/brand-kit/faq-accordion"
import { CategoryCard } from "@/components/brand-kit/category-card"
import { ProductShareButtons } from "@/components/blocks/product-share-buttons"
// Type-only import (erased at compile time — no server code enters the bundle)
import type { CollectionFacets } from "@/lib/collection-page-data"
import { inChartRange, type ChartRange } from "@/lib/collection-chart-range"
import type { CollectionCategoryStats } from "@/lib/collection-category-stats"
import { CollectionNumbersBand } from "./_components/collection-numbers-band"
import { CollectionTypeComparison } from "./_components/collection-type-comparison"
import type { BreadcrumbTrail } from "@/lib/breadcrumbs"
import type { Badge } from "@/components/badges/config"
import { formatCents } from "@/lib/format-utils"
import type { HeadCollectionGuide } from "@/lib/collections/head-collection-guide"
import { collectionHeadline, headTermIntro, widthTermIntro, type WidthAnswerFacts } from "@/lib/seo/head-term-headlines"
import {
  Accessibility, AlignHorizontalSpaceAround, ArrowDown, ArrowUp, Award,
  Battery, Beer, Bell, Box, Building2, Calculator, Camera, ChefHat,
  CheckCircle, Circle, CloudRain, Columns, Cpu, Crown, DollarSign,
  DoorOpen, Droplets, Eye, Factory, Flag, Flame, Gauge, Gem, GlassWater,
  GraduationCap, Hexagon, Home, Layers, LayoutGrid, LayoutPanelLeft,
  Leaf, Lock, Maximize2, Mic, Minimize2, Moon, Move, Package, Paintbrush,
  Palette, RefreshCw, Ruler, Scale, Settings, Shield, ShoppingCart,
  Smartphone, Snowflake, Sparkles, Square, Sun, Thermometer, TrendingUp,
  Truck, Users, Volume2, Warehouse, Wifi, Wind, Wine, Wrench, Zap,
} from "lucide-react"

// Covers every icon name present in hub_pages.features (DB-verified 07/16/2026;
// 67 distinct names). Unknown names fall back to a REAL icon, never raw text.
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Accessibility, AlignHorizontalSpaceAround, ArrowDown, ArrowUp, Award,
  Battery, Beer, Bell, Box, Building2, Calculator, Camera, ChefHat,
  CheckCircle, Circle, CloudRain, Columns, Cpu, Crown, DollarSign,
  DoorOpen, Droplets, Eye, Factory, Flag, Flame, Gauge, Gem, GlassWater,
  GraduationCap, Hexagon, Home, Layers, LayoutGrid, LayoutPanelLeft,
  Leaf, Lock, Maximize2, Mic, Minimize2, Moon, Move, Package, Paintbrush,
  Palette, RefreshCw, Ruler, Scale, Settings, Shield, ShoppingCart,
  Smartphone, Snowflake, Sparkles, Square, Sun, Thermometer, TrendingUp,
  Truck, Users, Volume2, Warehouse, Wifi, Wind, Wine, Wrench, Zap,
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DBProduct = {
  id: string
  brand: string
  model: string | null
  seo_slug: string | null
  canonical_url: string | null
  title: string
  type: string | null
  images: string[] | null
  lowest_price_cents: number | null
  average_price_30d_cents: number | null
  in_stock_count: number | null
  capacity_cuft: number | null
  /** Item 155: exterior width (in) — drives the Width facet's client predicate. */
  width_in: number | null
  /** Item 46 lane: normalized door-style label ("French Door", …) — exact-match
   *  predicate for the Door Style facet. */
  door_style: string | null
  /** Item 46 lane: wine/beverage bottle count — drives the Bottle Capacity
   *  facet's client bucket predicate. */
  bottle_capacity: number | null
  /** Item 46 tail (FRIDGE-33): beverage can count — drives the Can Capacity
   *  facet's client bucket predicate. Optional so rows from a stale cache
   *  entry (pre-field) degrade to unknown rather than crashing. */
  can_capacity?: number | null
  color: string | null
  finish: string | null
  tags: string[] | null
  features: string[] | null
  created_at: string | null
  /**
   * Canonical server-computed badge set (docs/Page-Types-And-Templates.md §6),
   * resolved server-side from the raw DB row (lib/collection-page-data.ts).
   * The grid renders this array verbatim — Real-or-Hidden — and never
   * re-derives membership client-side. Optional so rows from a stale cache
   * entry (pre-field) degrade to no badges rather than crashing.
   */
  badges?: Badge[]
  /**
   * FRIDGE-130: the product's cheapest DISPLAYABLE offer (the PDP's own gate),
   * read server-side in lib/collection-page-data.ts. Present → the grid
   * card's "See Deal" CTA is the monetized /r/ link; null/absent (no offer, or
   * a stale pre-field cache row) → the CTA stays the internal PDP link. Never
   * rebuilt client-side.
   */
  offerId?: string | null
  /** Display name of that offer's retailer (nullable) — CTA tooltip only. */
  retailerName?: string | null
  /** FRIDGE-1279 — the card's FIN chip; optional so a stale pre-field cache row shows none. */
  fin?: string | null
}

export type BrandKitFeature = {
  icon: string
  title: string
  description: string
}

export type BrandKitFAQ = {
  question: string
  answer: string
}

type AvailableBrand = {
  name: string
  slug: string
  logo: string | null
  productCount: number
}

interface CollectionBrandKitClientProps {
  slug: string
  collection: {
    title: string
    description: string | null
    collection_type?: string
  }
  /**
   * The page's ONE breadcrumb trail, built server-side by
   * lib/breadcrumbs.ts collectionBreadcrumbs() and ALSO fed to the
   * BreadcrumbList schema in collection-page-render.tsx — the visible
   * breadcrumb below renders this exact array (FRIDGE-269).
   */
  breadcrumbTrail: BreadcrumbTrail
  /** Optional editorial features (e.g. from hub_pages). Renders "What to Look For" when present. */
  features?: BrandKitFeature[]
  /** Optional FAQ items (e.g. from hub_pages). Renders FAQ accordion when present. */
  faqs?: BrandKitFAQ[]
  /**
   * Owner-approved answer-block Q&As from collection_faqs (consolidation plan
   * §6), pre-escaped/formatted server-side. Renders the Quick Answers section
   * when present — 0 rows (today's state) renders nothing.
   */
  answerFaqs?: BrandKitFAQ[]
  /**
   * W14 payload diet: ONLY the visible page's rows (server-sliced). The full
   * list is fetched lazily from ./products-data on the first filter/sort/
   * in-place-page interaction — landings pay ~20 rows instead of ~2,000.
   */
  products: DBProduct[]
  totalProducts: number
  page: number
  totalPages: number
  /** Server-computed sidebar facets derived from the FULL product list (W14). */
  facets: CollectionFacets
  /**
   * FRIDGE-875 — the merchandising rails, rendered on the SERVER and handed in
   * as a slot.
   *
   * It replaced four client-derived carousels, three of which ranked nothing:
   * "Top Rated" sorted the members by `in_stock_count`, "Best Seller" was
   * `[...products].reverse()` — the members in the opposite of an arbitrary
   * order — and "Cool Right Now" was `slice(0, 12)` under the line "The
   * refrigerators defining modern luxury kitchens today", printed over freezers
   * and wine coolers alike. The fourth, "Best Value Picks", was a real filter
   * (below its own 30-day average) but ranked nothing inside it, and the
   * measured "Biggest Price Drops" rail supersedes it.
   *
   * A ReactNode rather than data because the rails rank over the collection's
   * FULL member list — which the server holds and the client does not until
   * someone filters — and because every card is then server-rendered HTML that
   * crawlers and answer engines can read.
   */
  railsSlot?: React.ReactNode
  /** Brands carrying products in this collection, pre-computed on the server. */
  availableBrands?: AvailableBrand[]
  /**
   * seo_disposition gate for the "Browse by Category" related block (plan
   * §0.16): collection slugs the server page confirmed
   * `status='active' ∧ seo_disposition='indexed'` via
   * `lib/collection-links.filterLinkableCollections`. EXPLORE_CATEGORIES
   * cards whose slug is absent self-hide (fail-closed, never a gated link).
   * null/undefined ⇒ ungated passthrough (legacy callers).
   */
  linkableCollectionSlugs?: readonly string[] | null
  /**
   * FRIDGE-585 — token-sibling collections computed server-side (indexed +
   * active only). Lateral internal links so long-tail collections pass
   * equity sideways instead of only linking the head terms.
   */
  siblingCollections?: readonly { slug: string; title: string }[] | null
  /**
   * FRIDGE-401 Step 4 — head-category measurements (true tracked-model
   * count past the fetch cap, price median/range, live offer aggregate,
   * ENERGY STAR share, capacity span, type-vs-type rows), computed
   * server-side by lib/collection-category-stats.ts for the allow-listed
   * head slugs only. null on every other collection ⇒ neither module
   * renders (Real-or-Hidden).
   */
  categoryStats?: CollectionCategoryStats | null
  /**
   * FRIDGE-480 saturation truth from the bundle: the SQL fetch hit
   * PostgREST's row cap, so `totalProducts` is a floor, not a measurement.
   * The hero renders "over N models" then — the same discipline the FAQ
   * answers already apply (lib/collection-faqs.ts).
   */
  queryTopics?: CollectionQueryTopic[]
  /** FRIDGE-902: the question the single answer answers, printed as its heading. */
  queryQuestion?: string | null
  queryAnswer?: string | null
  queryExamples?: CollectionQueryExample[]
  querySources?: CollectionQueryExample[]
  /** FRIDGE-1423: the head collection's price, style, size and facet guide; null elsewhere. */
  headGuide?: HeadCollectionGuide | null
  countIsFloor?: boolean
  /**
   * FRIDGE-1483: on a base width page, the Dimensions Report facts the
   * answer-first sentence states beside the page's own count and median;
   * null on every other collection.
   */
  widthFacts?: (WidthAnswerFacts & { inches: number }) | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Canonical display rule (owner ruling 07/25/2026): whole dollars drop ".00",
// real cents render exactly. Shared implementation in lib/format-utils.
function priceDisplay(cents: number | null): string | undefined {
  if (!cents || cents <= 0) return undefined
  return formatCents(cents)
}

/** Render a Lucide icon by name. Never prints a raw icon NAME as text: an
 *  unknown value renders as an emoji only if it actually IS one (non-ASCII),
 *  else falls back to a neutral Sparkles icon. */
function LucideIcon({ name, className }: { name: string; className?: string }) {
  const IconComp = ICON_MAP[name]
  if (IconComp) {
    return <IconComp className={className || "w-5 h-5"} />
  }
  // A real emoji (non-ASCII) may be stored directly — render it.
  if (name && /[^\x20-\x7e]/.test(name)) {
    return <span className="text-lg">{name}</span>
  }
  // Unknown ASCII identifier (an unmapped icon name): neutral icon, never text.
  return <Sparkles className={className || "w-5 h-5"} />
}

// ---------------------------------------------------------------------------
// Hardcoded data (user-approved: brand logos + explore categories stay static)
// ---------------------------------------------------------------------------

const BRAND_LOGOS: { name: string; slug: string; logo: string | null }[] = [
  { name: "Samsung", slug: "samsung", logo: `/brand-logos/Samsung-Logo-Clean.png?v=1765060155690` },
  { name: "LG", slug: "lg", logo: `/brand-logos/lg-logo.png` },
  { name: "Whirlpool", slug: "whirlpool", logo: `/brand-logos/whirlpool-logo.png` },
  { name: "GE", slug: "ge", logo: `/brand-logos/ge-logo.png?v=1771950077360` },
  { name: "Frigidaire", slug: "frigidaire", logo: `/brand-logos/frigidaire-logo.png` },
  { name: "KitchenAid", slug: "kitchenaid", logo: `/brand-logos/kitchenaid-logo.png` },
  { name: "Maytag", slug: "maytag", logo: `/brand-logos/maytag-logo.png` },
  { name: "Bosch", slug: "bosch", logo: `/brand-logos/bosch-logo.png` },
  { name: "Electrolux", slug: "electrolux", logo: `/brand-logos/electrolux-logo.png?v=1771951889845` },
  { name: "Kenmore", slug: "kenmore", logo: `/brand-logos/kenmore-logo.png` },
  { name: "Sub-Zero", slug: "sub-zero", logo: `/brand-logos/sub-zero-logo.png` },
  { name: "Thermador", slug: "thermador", logo: `/brand-logos/thermador-logo.png` },
  { name: "Fisher & Paykel", slug: "fisher-paykel", logo: `/brand-logos/fisher-paykel-logo.png?v=1771951899435` },
  { name: "Amana", slug: "amana", logo: `/brand-logos/amana-logo.png` },
  { name: "Haier", slug: "haier", logo: `/brand-logos/haier-logo.png?v=1771951934300` },
  { name: "Hisense", slug: "hisense", logo: `/brand-logos/hisense-logo.png?v=1771952279740` },
  { name: "Danby", slug: "danby", logo: `/brand-logos/danby-logo.png?v=1771950079510` },
  { name: "Midea", slug: "midea", logo: `/brand-logos/midea-logo.svg?v=1771950082424` },
  { name: "Viking", slug: "viking", logo: `/brand-logos/viking-logo.svg?v=1771970473091` },
  { name: "Cuisinart", slug: "cuisinart", logo: `/brand-logos/cuisinart-logo.png?v=1771951886181` },
]

const EXPLORE_CATEGORIES: { label: string; href: string; imageUrl: string }[] = [
  { label: "French Door Refrigerators", href: "/collections/french-door-refrigerators", imageUrl: "https://images.unsplash.com/photo-1588854337115-1c67d9247e4d?w=600&q=80&auto=format&fit=crop" },
  { label: "Side-by-Side Refrigerators", href: "/collections/side-by-side-refrigerators", imageUrl: "https://images.unsplash.com/photo-1721613877687-c9099b698faa?w=600&q=80&auto=format&fit=crop" },
  { label: "Top Freezer Refrigerators", href: "/collections/top-freezer-refrigerators", imageUrl: "https://images.unsplash.com/photo-1630459065645-549fe5a56db4?w=600&q=80&auto=format&fit=crop" },
  { label: "Bottom Freezer Refrigerators", href: "/collections/bottom-freezer-refrigerators", imageUrl: "https://images.thdstatic.com/productImages/bdc9483b-8578-469f-b68c-1f40eae38bf9/svn/monochromatic-stainless-steel-amana-bottom-freezer-refrigerators-abb2224brm-64_600.jpg" },
  { label: "Counter Depth Refrigerators", href: "/collections/counter-depth-refrigerators", imageUrl: "https://images.unsplash.com/photo-1722859178634-ccc8ea5680d2?w=600&q=80&auto=format&fit=crop" },
  { label: "Mini Fridges", href: "/collections/mini-fridges", imageUrl: "https://images.unsplash.com/photo-1540961403310-79825242906e?w=600&q=80&auto=format&fit=crop" },
  { label: "Built-In Refrigerators", href: "/collections/built-in-refrigerators", imageUrl: "https://images.unsplash.com/photo-1610733374054-59454fe657cd?w=600&q=80&auto=format&fit=crop" },
  { label: "Smart Fridges", href: "/collections/smart-fridges", imageUrl: "https://images.unsplash.com/photo-1670272506253-3a185e06d102?w=600&q=80&auto=format&fit=crop" },
  { label: "Wine Coolers", href: "/collections/wine-fridges", imageUrl: "https://images.unsplash.com/photo-1578911373434-0cb395d2cbfb?w=600&q=80&auto=format&fit=crop" },
  { label: "Garage Refrigerators", href: "/collections/garage-refrigerators", imageUrl: "https://images.unsplash.com/photo-1635108198854-26645ffe6714?w=600&q=80&auto=format&fit=crop" },
]

// ---------------------------------------------------------------------------
// Client-side filter helpers (matches prototype)
// ---------------------------------------------------------------------------

const CAPACITY_RANGES: Record<string, [number, number]> = {
  "Under 20 cu. ft.": [0, 20],
  "20-24 cu. ft.": [20, 24],
  "25-27 cu. ft.": [25, 27],
  "28-30 cu. ft.": [28, 30],
  "Over 30 cu. ft.": [30, Infinity],
  "Under 5 cu. ft.": [0, 5],
  "5-10 cu. ft.": [5, 10],
  "10-15 cu. ft.": [10, 15],
  "15+ cu. ft.": [15, Infinity],
}

// Item 155: Width facet buckets. Labels MUST match FACET_WIDTH_BUCKETS in
// lib/collection-page-data.ts verbatim (server derives the options, this maps
// each label back to its numeric range for the client predicate). Top bucket
// bounded at 72 so a garbage width never satisfies "36 in. & wider".
const WIDTH_RANGES: Record<string, [number, number]> = {
  "Under 24 in.": [0, 24],
  "24–28 in.": [24, 28],
  "28–30 in.": [28, 30],
  "30–33 in.": [30, 33],
  "33–36 in.": [33, 36],
  "36 in. & wider": [36, 72],
}

// Item 46 lane: Bottle Capacity buckets. Labels MUST match FACET_BOTTLE_BUCKETS
// in lib/collection-page-data.ts verbatim (server derives which options exist,
// this maps each label back to its numeric range for the client predicate).
const FACET_BOTTLE_RANGES: Record<string, [number, number]> = {
  "Under 20 bottles": [0, 20],
  "20–40 bottles": [20, 40],
  "40–60 bottles": [40, 60],
  "60–100 bottles": [60, 100],
  "100+ bottles": [100, Infinity],
}

// Item 46 tail (FRIDGE-33): Can Capacity buckets. Labels MUST match
// FACET_CAN_BUCKETS in lib/collection-page-data.ts verbatim (server derives
// which options exist, this maps each label back to its numeric range).
const FACET_CAN_RANGES: Record<string, [number, number]> = {
  "Under 60 cans": [0, 60],
  "60–100 cans": [60, 100],
  "100–150 cans": [100, 150],
  "150+ cans": [150, Infinity],
}

// ---------------------------------------------------------------------------
// Filter predicate (shared by the product grid AND the sidebar facet counts)
// ---------------------------------------------------------------------------

/** Facet groups the predicate can be asked to skip. Skipping a group is how a
 *  faceted-count pass keeps that group's own options selectable: each group is
 *  counted against the products passing ALL OTHER active groups. */
type FacetGroup =
  | "price" | "brands" | "capacity" | "width" | "doorStyle"
  | "bottleCapacity" | "canCapacity" | "energyStar" | "features" | "colors"

/** Color/finish match — full label, else every significant (≥4-char) word must
 *  appear in the product's color+finish+title text. Shared so the grid filter
 *  and the sidebar Color facet counts agree exactly. */
function productMatchesColor(p: DBProduct, colorLabel: string): boolean {
  const colorText = `${p.color || ""} ${p.finish || ""} ${p.title || ""}`.toLowerCase()
  const lower = colorLabel.toLowerCase()
  if (colorText.includes(lower)) return true
  const words = lower.split(" ").filter((w) => w.length >= 4)
  return words.length > 0 && words.every((w) => colorText.includes(w))
}

/** Does a product satisfy the active sidebar filters? `skip` omits ONE facet
 *  group — used by the faceted-count pass so a group is tallied against the set
 *  narrowed by every OTHER active group (its own options stay selectable). */
export function productPassesFilters(
  p: DBProduct,
  filters: CollectionFilters,
  priceBounds: [number, number],
  skip?: FacetGroup,
): boolean {
  // Price: null/0-price rows are affiliate "See Deal" items; the Include-"See
  // Deal" checkbox is the control (checked = always shown). At the max bound the
  // upper end is open (catalog can carry products above the facet cap).
  if (skip !== "price") {
    const cents = p.lowest_price_cents
    if (cents == null || cents <= 0) {
      if (!filters.includeSeeDeal) return false
    } else {
      const priceDollars = cents / 100
      const maxIsOpenEnded = filters.priceRange[1] >= priceBounds[1]
      if (priceDollars < filters.priceRange[0] || (!maxIsOpenEnded && priceDollars > filters.priceRange[1])) return false
    }
  }
  if (skip !== "brands" && !filters.allBrands && filters.brands.length > 0) {
    // EXACT match (case-insensitive) on the verbatim DB brand string.
    const pb = (p.brand || "").trim().toLowerCase()
    if (!filters.brands.some((b) => pb === b.trim().toLowerCase())) return false
  }
  if (skip !== "capacity" && filters.capacity.length > 0) {
    // EXCLUDES unknown-capacity rows; title extraction mirrors the server.
    const cap = p.capacity_cuft ?? extractCapacityFromTitle(p.title || "")
    if (cap == null) return false
    const matchesAny = filters.capacity.some((bucket) => {
      const range = CAPACITY_RANGES[bucket]
      if (!range) return false
      return cap >= range[0] && cap < range[1]
    })
    if (!matchesAny) return false
  }
  if (skip !== "width" && filters.width.length > 0) {
    // Item 155. EXCLUDES unknown-width rows (trusted only from the width_in column).
    const w = p.width_in
    if (w == null || !(w > 0)) return false
    const matchesAny = filters.width.some((bucket) => {
      const range = WIDTH_RANGES[bucket]
      if (!range) return false
      return w >= range[0] && w < range[1]
    })
    if (!matchesAny) return false
  }
  if (skip !== "doorStyle" && filters.doorStyle.length > 0) {
    // Item 46 lane. Exact-match on the server-normalized label; EXCLUDES rows
    // with no parseable door style (never guessed).
    if (!p.door_style || !filters.doorStyle.includes(p.door_style)) return false
  }
  if (skip !== "bottleCapacity" && filters.bottleCapacity.length > 0) {
    // Item 46 lane. EXCLUDES unknown-count rows (mirrors width).
    const b = p.bottle_capacity
    if (b == null || !(b > 0)) return false
    const matchesAny = filters.bottleCapacity.some((bucket) => {
      const range = FACET_BOTTLE_RANGES[bucket]
      if (!range) return false
      return b >= range[0] && b < range[1]
    })
    if (!matchesAny) return false
  }
  if (skip !== "canCapacity" && filters.canCapacity.length > 0) {
    // Item 46 tail (FRIDGE-33). EXCLUDES unknown-count rows only while a can
    // bucket is actively selected (mirrors bottleCapacity); with no selection
    // null-can products stay visible.
    const c = p.can_capacity
    if (c == null || !(c > 0)) return false
    const matchesAny = filters.canCapacity.some((bucket) => {
      const range = FACET_CAN_RANGES[bucket]
      if (!range) return false
      return c >= range[0] && c < range[1]
    })
    if (!matchesAny) return false
  }
  if (skip !== "energyStar" && filters.energyStar && !(p.tags || []).includes("energy-star")) return false
  if (skip !== "features" && filters.features.length > 0) {
    const pFeatures = (p.features || []).map((f) => f.toLowerCase())
    const matchesAll = filters.features.every((sf) =>
      pFeatures.some((pf) => pf.includes(sf.toLowerCase()))
    )
    if (!matchesAll) return false
  }
  if (skip !== "colors" && filters.colors.length > 0) {
    if (!filters.colors.some((c) => productMatchesColor(p, c))) return false
  }
  return true
}

// ---------------------------------------------------------------------------
// Breadcrumb — NOT derived here. The trail arrives as the `breadcrumbTrail`
// prop from lib/breadcrumbs.ts (collectionBreadcrumbs), the same array the
// server hands the BreadcrumbList schema. The slug-keyword classifier that
// used to live here ("freezer" in slug → Freezers) filed Top Freezer and
// Bottom Freezer REFRIGERATORS under Freezers and put a collection under
// itself ("Refrigerators / Refrigerators"); a real category level is Phase 2
// of FRIDGE-269 (needs the taxonomy ruling, FRIDGE-57).
// ---------------------------------------------------------------------------
// True Nature Meats promo — a single on-theme branded grid cell shown on
// standalone-freezer collections (bulk-meat delivery is a natural fit for a
// chest/upright freezer). Renders one grid cell matching the product-card
// footprint, with an "Ad" disclosure, the FlexOffers-approved tracking link
// (30% commission, advertiser 247377), and rel="sponsored nofollow noopener".
// ---------------------------------------------------------------------------
function FreezerMeatPromoCard() {
  return (
    <a
      href={TRUE_NATURE_MEATS_AD.trackingUrl}
      target="_blank"
      rel="sponsored nofollow noopener"
      className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition-shadow hover:shadow-lg"
    >
      <span className="absolute left-3 top-3 z-10 rounded bg-white/95 px-2 py-0.5 text-[11px] font-medium text-gray-500 shadow-sm">
        Ad
      </span>
      <div className="flex items-center justify-center bg-white p-4" style={{ aspectRatio: "1 / 1" }}>
        <img
          src={TRUE_NATURE_MEATS_AD.imageUrl}
          alt={TRUE_NATURE_MEATS_AD.imageAlt}
          className="max-h-full max-w-full object-contain"
          loading="lazy"
        />
      </div>
      <div className="flex flex-1 flex-col border-t border-gray-100 px-4 py-3">
        <p className="text-[13px] font-semibold text-gray-900">True Nature Meats</p>
        <p className="mt-1 text-xs leading-snug text-gray-500">
          Grass-fed, chef-crafted meat — flash-frozen and delivered. Fill your new freezer.
        </p>
        <span className="mt-3 inline-flex w-full items-center justify-center rounded-lg bg-[#0469f0] px-3 py-2 text-xs font-semibold text-white">
          Shop True Nature Meats
        </span>
      </div>
    </a>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CollectionBrandKitClient({
  slug,
  collection,
  breadcrumbTrail,
  features = [],
  faqs = [],
  answerFaqs = [],
  products,
  totalProducts,
  page: serverPage,
  totalPages: serverTotalPages,
  availableBrands: availableBrandsFromServer,
  linkableCollectionSlugs,
  siblingCollections,
  facets,
  railsSlot = null,
  categoryStats = null,
  countIsFloor = false,
  widthFacts = null,
  queryTopics = [],
  queryQuestion = null,
  queryAnswer = null,
  queryExamples = [],
  querySources = [],
  headGuide = null,
}: CollectionBrandKitClientProps) {
  // "Browse by Category" indexed gate (plan §0.16): drop any category card
  // whose collection is not server-confirmed indexed. All EXPLORE_CATEGORIES
  // slugs are curated-T1 chrome candidates, so an indexed card always passes;
  // a gated/visible_unindexed one self-hides. Null set ⇒ passthrough.
  const visibleExploreCategories = useMemo(
    () =>
      linkableCollectionSlugs
        ? keepIndexedCollectionLinks(EXPLORE_CATEGORIES, new Set(linkableCollectionSlugs))
        : EXPLORE_CATEGORIES,
    [linkableCollectionSlugs]
  )
  const [filters, setFilters] = useState<CollectionFilters | null>(null)
  // FRIDGE-1278 — a section chosen on the "by the Numbers" chart; applied on
  // top of the sidebar filters, shown as a removable chip.
  const [chartRange, setChartRange] = useState<ChartRange | null>(null)
  const [sortBy, setSortBy] = useState("featured")
  // Landing on ?page=N starts on that page — the server slices those rows and
  // emits the "– Page N" title + self-referencing ?page=N canonical, so state
  // can init straight from the server prop (no URL-sniffing effect needed).
  const [clientPage, setClientPage] = useState(serverPage)

  // ── W14 lazy full list ─────────────────────────────────────────────────────
  // `products` is only the visible page's rows. The FULL filtered list (same
  // cached bundle, same row order the server sliced from) is fetched from
  // ./products-data the first time the user filters, sorts, or pages in place.
  const [clientMode, setClientMode] = useState(false)
  const [allProducts, setAllProducts] = useState<DBProduct[] | null>(null)
  const [fetchFailed, setFetchFailed] = useState(false)
  const fetchStartedRef = useRef(false)
  useEffect(() => {
    if (!clientMode || fetchStartedRef.current) return
    fetchStartedRef.current = true
    fetch(`/collections/${encodeURIComponent(slug)}/products-data`)
      .then((r) => {
        if (!r.ok) throw new Error(`products-data ${r.status}`)
        return r.json()
      })
      .then((d) => setAllProducts(Array.isArray(d?.products) ? d.products : []))
      .catch(() => setFetchFailed(true))
  }, [clientMode, slug])

  // Pagination ↔ URL sync (SEO + shareability): changing pages rewrites the
  // URL via replaceState so the address bar always reflects the visible page
  // without a full navigation.
  const gotoPage = (newPage: number) => {
    // Full list unavailable (fetch failed) → degrade to a real navigation so
    // pagination always works; the pager's hrefs point at the same URLs.
    if (fetchFailed) {
      window.location.assign(newPage > 1 ? `/collections/${slug}?page=${newPage}` : `/collections/${slug}`)
      return
    }
    setClientMode(true)
    setClientPage(newPage)
    const url = new URL(window.location.href)
    if (newPage > 1) url.searchParams.set("page", String(newPage))
    else url.searchParams.delete("page")
    window.history.replaceState(null, "", url.toString())
  }

  const collectionType: "refrigerator" | "freezer" =
    collection.collection_type === "freezer" ? "freezer" : "refrigerator"

  // ── Fully data-derived facets — now SERVER-computed over the FULL list ─────
  // (lib/collection-page-data.ts computeFacets, W14). The client receives only
  // one page of rows, so deriving facets here would see 20 products; the
  // server derives them from the whole collection and ships the small result.
  const {
    priceBounds,
    hasPricedProducts,
    hasSeeDealProducts,
    capacityOptions,
    widthOptions,
    doorStyleOptions,
    bottleCapacityOptions,
    colorOptions,
    featureOptions: rawFeatureOptions,
    hasEnergyStar,
  } = facets
  const featureOptions = useMemo(() => toFeatureFacetOptions(rawFeatureOptions), [rawFeatureOptions])
  const featureLabels = useMemo(() => new Map(featureOptions.map((f) => [f.name, f.displayLabel ?? f.name])), [featureOptions])
  // Item 46 tail (FRIDGE-33): optional so a stale pre-field cached facets
  // object degrades to a hidden Can Capacity section rather than crashing.
  const canCapacityOptions = facets.canCapacityOptions ?? []
  // Sidebar brand facet — reconstruct one row per product from the server's
  // [brand, count] pairs (the sidebar derives its brand list from rows).
  const sidebarBrandRows = useMemo(
    () => facets.brandCounts.flatMap(([name, count]) => Array.from({ length: count }, () => ({ brand: name }))),
    [facets.brandCounts],
  )

  // Active filter count for badge / chip bar
  const activeFiltersCount = (chartRange ? 1 : 0) + (filters
    ? (!filters.allBrands ? filters.brands.length : 0) +
      filters.capacity.length +
      filters.width.length +
      filters.doorStyle.length +
      filters.bottleCapacity.length +
      filters.canCapacity.length +
      filters.features.length +
      filters.colors.length +
      (filters.energyStar ? 1 : 0) +
      (!filters.includeSeeDeal ? 1 : 0) +
      (filters.priceRange[0] > priceBounds[0] || filters.priceRange[1] < priceBounds[1] ? 1 : 0)
    : 0)

  // Any real filter/sort interaction switches to client mode → lazy-fetch the
  // full list once, then filter/sort/paginate exactly as before over it.
  // Latched during render rather than from an effect
  // (react-hooks/set-state-in-effect). It only ever moves false -> true, so the
  // guard is the latch itself. The effect version spent one frame still in
  // server mode after the first facet click.
  if (!clientMode && (activeFiltersCount > 0 || sortBy !== "featured")) {
    setClientMode(true)
  }

  // Until the full list lands, filter over the server rows so the grid never
  // blanks (server order and rows match the full list's corresponding slice).
  const baseProducts = clientMode && allProducts ? allProducts : products

  // Client-side filtering. The per-product predicate lives at module scope
  // (productPassesFilters) so the sidebar facet-count pass below applies the
  // exact same matching rules — one source of truth for "does this product
  // match the filters".
  const filteredProducts = useMemo(() => {
    let list = filters
      ? baseProducts.filter((p) => productPassesFilters(p, filters, priceBounds))
      : [...baseProducts]
    // FRIDGE-1278 — the chart section's own membership rule, so the grid lists
    // exactly the models the section counted.
    if (chartRange) list = list.filter((p) => inChartRange(p, chartRange))

    // Client-side sort. Null/0-price (affiliate "See Deal") items always sort
    // to the END of a price sort — never masquerade as the cheapest.
    if (sortBy === "price-asc") {
      list.sort((a, b) => {
        const av = a.lowest_price_cents && a.lowest_price_cents > 0 ? a.lowest_price_cents : Infinity
        const bv = b.lowest_price_cents && b.lowest_price_cents > 0 ? b.lowest_price_cents : Infinity
        return av - bv
      })
    } else if (sortBy === "price-desc") {
      list.sort((a, b) => {
        const av = a.lowest_price_cents && a.lowest_price_cents > 0 ? a.lowest_price_cents : -Infinity
        const bv = b.lowest_price_cents && b.lowest_price_cents > 0 ? b.lowest_price_cents : -Infinity
        return bv - av
      })
    } else if (sortBy === "newest") {
      list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
    }
    // "featured" keeps the server order

    return list
  }, [baseProducts, filters, sortBy, priceBounds, chartRange])

  // Reset to page 1 whenever filters or sort change
  const filterKey = (filters
    ? JSON.stringify([filters.brands, filters.capacity, filters.width, filters.doorStyle, filters.bottleCapacity, filters.canCapacity, filters.features, filters.colors, filters.energyStar, filters.priceRange, filters.allBrands, filters.includeSeeDeal])
    : "") + (chartRange ? JSON.stringify(chartRange) : "")
  // Reset to page 1 only on a REAL filter/sort change. Value-compared against
  // the previous key so neither the mount nor StrictMode's dev double-run
  // clobbers the ?page=N deep-link init above.
  const prevResetKeyRef = useRef<string | null>(null)
  useEffect(() => {
    const key = `${filterKey}|${sortBy}`
    if (prevResetKeyRef.current === key) return
    const isFirst = prevResetKeyRef.current === null
    prevResetKeyRef.current = key
    if (isFirst) return
    setClientPage(1)
    // A filter/sort change redefines the result set — drop any ?page from the URL.
    const url = new URL(window.location.href)
    if (url.searchParams.has("page")) {
      url.searchParams.delete("page")
      window.history.replaceState(null, "", url.toString())
    }
  }, [filterKey, sortBy])

  // W14 dual-mode pagination:
  //  - server mode (no interaction yet): `products` already IS the visible
  //    page's rows; the server totalPages drives the pager (crawlable hrefs
  //    unchanged).
  //  - client mode with the full list loaded: classic client-side slice over
  //    the filtered list. While the lazy fetch is in flight, keep showing the
  //    server rows so the grid never blanks.
  const PRODUCTS_PER_PAGE = 20
  const GRID_COLS = 4
  const hasFullList = clientMode && allProducts !== null
  // Hero count (item 6): fully live from the catalog. With no active filter it
  // is the collection's real DB total; once the visitor filters — and the full
  // list has landed so the number is exact, not a one-page undercount — it
  // becomes the count of the currently-filtered set and updates on every filter
  // change.
  const heroFiltered = activeFiltersCount > 0 && hasFullList
  const heroCount = heroFiltered ? filteredProducts.length : totalProducts
  // FRIDGE-1434: the head page's H1 and its answer-first sentence come from
  // one module; the sentence states this render's own count and median.
  const headTerm = collectionHeadline(slug)
  const introFacts = {
    term: headTerm?.term ?? '',
    models: heroCount,
    countIsFloor,
    medianPrice: categoryStats?.medianPrice ?? null,
    minPrice: categoryStats?.minPrice ?? null,
    maxPrice: categoryStats?.maxPrice ?? null,
  }
  // FRIDGE-1483: a base width page adds the Dimensions Report's standard-width
  // answer; every other head page keeps the count-and-median sentence.
  const answerFirst =
    headTerm && !heroFiltered
      ? widthFacts
        ? widthTermIntro({ ...introFacts, ...widthFacts })
        : headTermIntro(introFacts)
      : null

  // FRIDGE-136 — screen-reader announcement of the result count. Filtering
  // mutates the grid silently (no navigation, no URL change), so a polite
  // live region beside the grid says how many products now match. It starts
  // empty (nothing announced on load) and only ever changes after the visitor
  // touches a facet: "N products match your filters" while filtered, then
  // "Showing all N products" once every filter is cleared again.
  // Fully DERIVED — it was never independent state, only a value an effect
  // recomputed. `hasEverFiltered` carries the one genuinely stateful bit: the
  // region stays silent until a facet has actually been used, so it never
  // announces on first paint.
  const [hasEverFiltered, setHasEverFiltered] = useState(false)
  if (hasFullList && activeFiltersCount > 0 && !hasEverFiltered) {
    setHasEverFiltered(true)
  }
  const resultAnnouncement = useMemo(() => {
    if (!hasFullList) return ""
    if (activeFiltersCount > 0) {
      const n = filteredProducts.length
      return n === 0
        ? "No products match your filters"
        : `${n.toLocaleString()} ${n === 1 ? "product matches" : "products match"} your filters`
    }
    return hasEverFiltered ? `Showing all ${totalProducts.toLocaleString()} products` : ""
  }, [hasFullList, activeFiltersCount, filteredProducts.length, totalProducts, hasEverFiltered])

  // ── Live sidebar facet counts ──────────────────────────────────────────────
  // Every facet option's count reflects the CURRENTLY filtered set: each group
  // is tallied over the products passing all OTHER active groups (skip = its own
  // group), so a group's own options stay selectable — the standard faceted-nav
  // rule. Only meaningful once the FULL list has landed; until then the sidebar
  // keeps the server's full-collection counts (facetCounts = null). Recomputes
  // whenever the active filters change (memo key includes `filters`).
  const facetCounts = useMemo<FacetCounts | null>(() => {
    if (!hasFullList || !filters) return null
    const f = filters
    const tally = (
      skip: FacetGroup,
      assign: (p: DBProduct, add: (label: string) => void) => void,
    ): Record<string, number> => {
      const counts: Record<string, number> = {}
      for (const p of baseProducts) {
        if (!productPassesFilters(p, f, priceBounds, skip)) continue
        assign(p, (label) => { counts[label] = (counts[label] ?? 0) + 1 })
      }
      return counts
    }

    const brands = tally("brands", (p, add) => {
      const b = (p.brand || "").trim()
      if (b) add(b)
    })
    const capacity = tally("capacity", (p, add) => {
      const cap = p.capacity_cuft ?? extractCapacityFromTitle(p.title || "")
      if (cap == null) return
      for (const bucket of capacityOptions) {
        const r = CAPACITY_RANGES[bucket]
        if (r && cap >= r[0] && cap < r[1]) { add(bucket); break }
      }
    })
    const width = tally("width", (p, add) => {
      const w = p.width_in
      if (w == null || !(w > 0)) return
      for (const bucket of widthOptions) {
        const r = WIDTH_RANGES[bucket]
        if (r && w >= r[0] && w < r[1]) { add(bucket); break }
      }
    })
    const doorStyle = tally("doorStyle", (p, add) => {
      if (p.door_style) add(p.door_style)
    })
    const bottleCapacity = tally("bottleCapacity", (p, add) => {
      const b = p.bottle_capacity
      if (b == null || !(b > 0)) return
      for (const bucket of bottleCapacityOptions) {
        const r = FACET_BOTTLE_RANGES[bucket]
        if (r && b >= r[0] && b < r[1]) { add(bucket); break }
      }
    })
    const canCapacity = tally("canCapacity", (p, add) => {
      const c = p.can_capacity
      if (c == null || !(c > 0)) return
      for (const bucket of canCapacityOptions) {
        const r = FACET_CAN_RANGES[bucket]
        if (r && c >= r[0] && c < r[1]) { add(bucket); break }
      }
    })
    const features = tally("features", (p, add) => {
      const pf = (p.features || []).map((x) => x.toLowerCase())
      for (const opt of featureOptions) {
        if (pf.some((x) => x.includes(opt.name.toLowerCase()))) add(opt.name)
      }
    })
    const colors = tally("colors", (p, add) => {
      for (const opt of colorOptions) {
        if (productMatchesColor(p, opt.name)) add(opt.name)
      }
    })
    let energyStar = 0
    for (const p of baseProducts) {
      if (productPassesFilters(p, f, priceBounds, "energyStar") && (p.tags || []).includes("energy-star")) energyStar++
    }

    return { brands, capacity, width, doorStyle, bottleCapacity, canCapacity, features, colors, energyStar }
  }, [
    hasFullList, baseProducts, filters, priceBounds,
    capacityOptions, widthOptions, bottleCapacityOptions, canCapacityOptions, featureOptions, colorOptions,
  ])
  const clientTotalPages = hasFullList
    ? Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE)
    : serverTotalPages
  const pageSlice = hasFullList
    ? filteredProducts.slice((clientPage - 1) * PRODUCTS_PER_PAGE, clientPage * PRODUCTS_PER_PAGE)
    : products
  // FRIDGE-1053: route from the current catalog slug; old canonical URLs
  // can name retired paths. A row without a destination cannot be a linked card.
  const routableProducts = pageSlice.flatMap(product => {
    const href = productPagePath(product)
    return href ? [{ ...product, href }] : []
  })
  // Keep every eligible row in the selected page. Trimming a partial visual
  // row silently loses products while counts, filters and pagination include them.
  const displayProducts = routableProducts

  // True Nature Meats bulk-meat promo: shown once, on the first page of a
  // standalone-freezer collection, only when there's a full first row to sit
  // behind. Kept out of the sitewide ad rotation so it never appears on
  // refrigerator/wine-cooler collections where it would be off-theme.
  const showFreezerMeatPromo =
    isFreezerSurface(slug) && clientPage === 1 && displayProducts.length >= GRID_COLS

  // Brands that actually carry products in this collection — pre-computed on the
  // server using the curated brands directory and filtered to brands with >= 2
  // products, so one-off Amazon listings don't inflate the count.
  const availableBrands = availableBrandsFromServer || []

  // Grid badges: the canonical server-computed `badges` array (spec §6) is
  // rendered verbatim by ProductGridCard's shared <BadgeList>. The previous
  // ad-hoc gridBadge() derivation (energy-star tag / ≥15% drop "lowest-price")
  // is retired — no surface re-derives badge membership (spec §5).

  return (
    <>
      <div className="flex max-w-[1440px] mx-auto w-full">
        {/* === SIDEBAR === */}
        <CollectionSidebarBrandkit
          onFiltersChange={(f) => setFilters(f)}
          collectionType={collectionType}
          products={sidebarBrandRows}
          priceBounds={priceBounds}
          hasPricedProducts={hasPricedProducts}
          hasSeeDealProducts={hasSeeDealProducts}
          capacityOptions={capacityOptions}
          widthOptions={widthOptions}
          doorStyleOptions={doorStyleOptions}
          bottleCapacityOptions={bottleCapacityOptions}
          canCapacityOptions={canCapacityOptions}
          colorOptions={colorOptions}
          featureOptions={featureOptions}
          hasEnergyStar={hasEnergyStar}
          facetCounts={facetCounts}
        />

        {/* === MAIN CONTENT === */}
        {/* FRIDGE-138 — id="main-content" is the header skip link's target. */}
        <main id="main-content" className="flex-1 px-4 md:px-8 py-6 min-w-0 bg-gray-50">
          {/* Breadcrumb — the server-built trail; identical to the BreadcrumbList schema */}
          <Breadcrumb trail={breadcrumbTrail} />

          {/* Collection Hero */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-none mb-3 text-gray-900">
                {headTerm?.h1 ?? collection.title}
              </h1>
              {/* Intro sentence — keyword-placement standard (plan item 15).
                  collections has no `description` column (the field arrives
                  null for every row today), so without this fallback the page
                  rendered no first sentence at all. The fallback states only
                  what the page does — cross-retailer price comparison over
                  the models listed below — with the head term leading. */}
              <p className="text-gray-700 max-w-xl text-sm md:text-base leading-relaxed">
                {collection.description || answerFirst ||
                  // Lowercasing the title made every brand read like a common
                  // noun mid-sentence ("ge refrigerators", "element freezers"
                  // — FRIDGE-521). applyBrandRules restores registered brand
                  // casing; brands deliberately absent from BRAND_RULES
                  // because their name is a common word (Element) remain the
                  // recorded residual of that trade.
                  // FRIDGE-480/401: when the bundle's fetch hit the row cap,
                  // `totalProducts` is a floor — say "over N", exactly as the
                  // FAQ answers already do (lib/collection-faqs.ts). A capped
                  // page never states a false exact count.
                  `Compare prices on ${applyBrandRules(collection.title.toLowerCase())} from major online appliance retailers${
                    heroCount > 0
                      ? ` — ${countIsFloor && !heroFiltered ? "over " : ""}${heroCount.toLocaleString()} ${heroCount === 1 ? "model" : "models"}${
                          heroFiltered
                            ? ` match your filters (of ${countIsFloor ? "over " : ""}${totalProducts.toLocaleString()} tracked by Fridge.com)`
                            : " tracked by Fridge.com"
                        }`
                      : ""
                  }.`}
              </p>
            </div>

            {/* Sort dropdown */}
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-widest">
              <span>Sort</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-white border border-gray-200 rounded-lg pl-3 pr-10 py-2 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ff9951]/40 focus:border-[#ff9951] transition-all appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_0.75rem_center] bg-no-repeat"
              >
                <option value="featured">Featured</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="newest">Newest</option>
              </select>
            </label>
          </div>

          {/* Share buttons — top */}
          <div className="mb-6">
            <ProductShareButtons
              productTitle={collection.title}
              pageType="collection"
            />
          </div>

          {/* FRIDGE-401 Step 4 — "{Category} by the Numbers". Head-category
              slugs carry the cached cross-retailer version; every other
              collection carries the page-faithful member aggregate
              (FRIDGE-1021). Null stats (no priced member) render nothing.
              Sits under the hero, exactly like the brand pages' band. */}
          <CollectionNumbersBand
            title={collection.title}
            stats={categoryStats}
            countIsFloor={countIsFloor}
            selectedRange={chartRange}
            onSelectRange={(r) => {
              setChartRange(r)
              // Take the shopper to the models the section stands for.
              if (r) requestAnimationFrame(() => document.getElementById("collection-results")?.scrollIntoView({ behavior: "smooth", block: "start" }))
            }}
          />

          {/* Active filter chip bar */}
          {activeFiltersCount > 0 && (
            <div id="collection-results" className="flex flex-wrap gap-2 mb-6 scroll-mt-28">
              {chartRange && (
                <FilterPill label={`Chart: ${chartRange.label}`} theme="blue" onRemove={() => setChartRange(null)} />
              )}
              {filters && !filters.allBrands && filters.brands.map((b) => (
                <FilterPill key={b} label={b} theme="orange" />
              ))}
              {filters && filters.capacity.map((c) => (
                <FilterPill key={c} label={c} theme="orange" />
              ))}
              {filters && filters.width.map((w) => (
                <FilterPill key={w} label={w} theme="orange" />
              ))}
              {filters && filters.doorStyle.map((d) => (
                <FilterPill key={d} label={d} theme="orange" />
              ))}
              {filters && filters.bottleCapacity.map((b) => (
                <FilterPill key={b} label={b} theme="orange" />
              ))}
              {filters && filters.canCapacity.map((c) => (
                <FilterPill key={c} label={c} theme="orange" />
              ))}
              {filters && filters.features.map((f) => (
                <FilterPill key={f} label={featureLabels.get(f) ?? "Feature"} theme="blue" />
              ))}
              {filters && filters.colors.map((c) => (
                <FilterPill key={c} label={c} theme="gray" />
              ))}
              {filters?.energyStar && <FilterPill label="ENERGY STAR" theme="blue" />}
            </div>
          )}

          {/* FRIDGE-136 — polite live region: the new result count is read out
              after every facet change (see resultAnnouncement). Present from
              first paint so assistive tech registers the region before it
              ever changes; visually hidden. */}
          <p role="status" aria-live="polite" aria-atomic="true" className="sr-only">
            {resultAnnouncement}
          </p>

          {/* FRIDGE-560 (owner, 08/25/2026): the affiliate sentence is REMOVED
              from collection type pages.

              What it was: FRIDGE-130 put the FTC disclosure adjacent to the
              monetized grid CTAs. That reasoning is recorded here rather than
              deleted, because the trade is real and should be visible if anyone
              revisits it — proximate disclosure is what FTC guidance prefers.

              What still carries it, re-verified 08/25/2026 by reading the
              source and loading a rendered collection page:
                - components/site-footer.tsx:24 — the identical sentence, on
                  EVERY page including this one
                - the PDP (app/products/[slug]/product-client.tsx)
                - /black-friday-refrigerator-deals
              So the disclosure is not gone from the site, nor from the surface
              where a purchase decision is actually made; it is gone from the
              collection grid, which is what was asked for.

              `gridHasMonetizedCards` was left declared-but-unused by the edit
              that removed the disclosure; it is now deleted rather than left
              as dead code with a comment explaining itself. */}

          {/* Product Grid */}
          {displayProducts.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
              {displayProducts.map((product, i) => (
                <Fragment key={product.id}>
                  {/* On standalone-freezer collections, the True Nature Meats
                      bulk-meat promo takes the start of the second row (page 1
                      only). See lib/ad-config isFreezerSurface / TRUE_NATURE_MEATS_AD. */}
                  {showFreezerMeatPromo && i === GRID_COLS && (
                    <div className="relative h-full">
                      <FreezerMeatPromoCard />
                    </div>
                  )}
                  <div className="relative h-full">
                    <ProductGridCard
                      images={product.images || []}
                      brand={product.brand}
                      title={product.title}
                      price={priceDisplay(product.lowest_price_cents)}
                      originalPrice={
                        product.average_price_30d_cents && product.lowest_price_cents && product.lowest_price_cents < product.average_price_30d_cents
                          ? priceDisplay(product.average_price_30d_cents)
                          : undefined
                      }
                      href={product.href}
                      priority={i < 4}
                      badges={product.badges}
                      offerId={product.offerId ?? null}
                      productId={product.id}
                      retailerName={product.retailerName ?? null}
                      fin={product.fin ?? null}
                      model={product.model ?? null}
                    />
                  </div>
                </Fragment>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white py-16 px-6 text-center">
              <p className="text-lg font-bold text-gray-900 mb-2">No products match your filters</p>
              <p className="text-sm text-gray-500">Try loosening your filter criteria to see more options.</p>
            </div>
          )}

          {/* FRIDGE-602's verified trust strip, MOVED below the grid
              (FRIDGE-669 centerpiece audit, 08/31/2026): a collection page's
              purpose is the live product grid — the one FILLER-LEADS verdict
              in the 20-type audit was this strip sitting between the H1 and
              the products. Reordered, never removed: the numbers are the
              owner's display set. */}
          <TrustStrip className="mt-6 mb-2" />

          {/* Pagination */}
          {clientTotalPages > 1 && (
            <Pagination
              currentPage={clientPage}
              totalPages={clientTotalPages}
              // Crawlable hrefs (traditional + LLM crawlers follow anchors, not
              // buttons); clicks still page client-side via onPageChange.
              hrefForPage={(n) => (n > 1 ? `/collections/${slug}?page=${n}` : `/collections/${slug}`)}
              onPageChange={(newPage) => {
                gotoPage(newPage)
                window.scrollTo({ top: 0, behavior: "smooth" })
              }}
            />
          )}

          {/* FRIDGE-1167: the collection introduction and products lead.
              Keep model-specific claims with their citations as supporting
              reading, never as the collection introduction. */}
          {!heroFiltered && (queryTopics.length > 0 || queryAnswer) && (
            <section aria-labelledby="collection-guidance-heading" className="mt-8 mb-8 border-t border-gray-200 pt-6">
              <h2 id="collection-guidance-heading" className="text-xl font-bold text-gray-900 mb-4">Model details and buying guidance</h2>
              {queryTopics.map(topic => (
                <section key={topic.question} aria-label={topic.question} className="max-w-xl mb-5">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">{topic.question}</h3>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">{topic.answer}</p>
                  <p className="mt-2 text-xs text-gray-600">
                    Sources: {topic.sources.map((source, index) => (
                      <span key={source.href}>{index > 0 && ' · '}<a href={source.href} className="text-[#0469f0] underline underline-offset-2">{source.label}</a></span>
                    ))}
                  </p>
                  {topic.examples.length > 0 && (
                    <nav aria-label={`${topic.question} Model examples`} className="mt-2 flex flex-wrap gap-2 text-sm">
                      {topic.examples.map(example => (
                        <Link key={example.href} href={example.href} className="inline-flex min-h-11 items-center text-[#0469f0] underline underline-offset-2">{example.label}</Link>
                      ))}
                    </nav>
                  )}
                </section>
              ))}
              {queryTopics.length === 0 && queryAnswer && (
                <div className="max-w-xl mb-3">
                  {queryQuestion && (
                    <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">{queryQuestion}</h3>
                  )}
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">{queryAnswer}</p>
                  {querySources.length > 0 && (
                    <p className="mt-2 text-xs text-gray-600">
                      Source: {querySources.map((source, index) => (
                        <span key={source.href}>{index > 0 && ' · '}<a href={source.href} className="text-[#0469f0] underline underline-offset-2">{source.label}</a></span>
                      ))}
                    </p>
                  )}
                  {queryExamples.length > 0 && (
                    <nav aria-label="Matching model examples" className="mt-2 text-sm">
                      <p className="text-gray-600 mb-1">Matching examples, listed alphabetically:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        {queryExamples.map(example => (
                          <li key={example.href}>
                            <Link href={example.href} className="inline-block py-2 text-[#0469f0] underline underline-offset-2">{example.label}</Link>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  )}
                </div>
              )}
            </section>
          )}

          {/* FRIDGE-1423: the head collection's price, style, size and facet
              guide. Collection links are gated by the indexed set; search
              links go to the catalog search the homepage uses. */}
          {!heroFiltered && headGuide && (
            <section aria-labelledby="collection-head-guide-heading" className="mt-8 mb-8 border-t border-gray-200 pt-6">
              <h2 id="collection-head-guide-heading" className="text-xl font-bold text-gray-900 mb-3">{headGuide.heading}</h2>
              {headGuide.intro && <p className="max-w-xl text-gray-700 text-sm md:text-base leading-relaxed mb-4">{headGuide.intro}</p>}
              {headGuide.groups.map((group) => {
                const links = group.links.filter((link) => {
                  const m = link.href.match(/^\/collections\/([^/?#]+)$/)
                  return !m || !linkableCollectionSlugs || linkableCollectionSlugs.includes(m[1])
                })
                if (links.length === 0) return null
                return (
                  <nav key={group.heading} aria-label={`${headGuide.heading}: ${group.heading}`} className="mb-3">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">{group.heading}</h3>
                    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      {links.map((link) => (
                        <li key={link.href}><Link href={link.href} className="inline-flex min-h-11 items-center text-[#0469f0] underline underline-offset-2">{link.label}</Link></li>
                      ))}
                    </ul>
                  </nav>
                )
              })}
            </section>
          )}

          {/* Shop by Manufacturer — only brands that actually carry products in this collection.
              For small lists (<= 20) show everything at once so the visible cards match the
              count in the header; for larger lists (e.g. refrigerators, mini-fridges) keep the
              incremental complete-row reveal so the section doesn't dominate the page. */}
          {availableBrands.length > 0 && (
            <BrandLogoGrid
              brands={availableBrands}
              initialCount={availableBrands.length <= 20 ? availableBrands.length : 10}
              expandStep={availableBrands.length <= 20 ? 0 : 5}
              completeRows
              searchable={availableBrands.length > 20}
              searchPlaceholder="Search brands..."
              title="Shop by Manufacturer"
              description={`${availableBrands.length} ${availableBrands.length === 1 ? "brand" : "brands"} carrying ${collection.title.toLowerCase()}.`}
            />
          )}
        </main>
      </div>

      {/* === BELOW-THE-FOLD — merchandising rails + editorial blocks === */}

      {/* FRIDGE-875 — the rails, server-rendered and handed in whole. They sit
          directly under the grid, where Amazon puts its own merchandising, and
          each one prints the rule it ranked by. Null when no rail cleared its
          three-member floor, which is the ordinary outcome for a small
          collection and renders nothing at all. */}
      {railsSlot}

      {/* FRIDGE-401 Step 4 — the type-vs-type comparison a retailer category
          page does not publish. Head-category slugs only (non-head stats
          carry no type rows and render nothing); rows are pre-measured and
          pre-gated server-side. */}
      <CollectionTypeComparison title={collection.title} stats={categoryStats} />

      {/* === ANSWER BLOCK (owner-approved collection_faqs; consolidation plan §6) ===
          Real-or-Hidden: renders only after the owner-gated apply step populates
          collection_faqs for this collection. Every item defaults open so each
          Q&A in the page's FAQPage schema is present in the served HTML —
          schema equals visible. */}
      {answerFaqs.length > 0 && (
        <section className="bg-white py-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8">
            <PageSectionHeader
              eyebrow="Quick Answers"
              title={`What to Know About ${collection.title}`}
            />
            <FAQAccordion
              items={answerFaqs}
              columns={1}
              defaultOpen={answerFaqs.map((_, i) => i)}
              bordered={false}
            />
          </div>
        </section>
      )}

      {/* === BUYING GUIDE (optional editorial enrichment) === */}
      {features.length > 0 && (
        <section className="bg-white py-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8">
            <SectionHeader
              eyebrow="Buying Guide"
              title={`What to Look For in ${collection.title}`}
              description="Key factors to consider before you buy."
              bordered
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.map((feat) => (
                <ConsiderationCard
                  key={feat.title}
                  icon={<LucideIcon name={feat.icon} className="w-5 h-5" />}
                  title={feat.title}
                  description={feat.description}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* === FAQ (optional editorial enrichment) === */}
      {faqs.length > 0 && (
        <section className="bg-gray-50 py-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8">
            {/* One heading for the block: the accordion's own default title is suppressed so the
                eyebrow, the H2 and an H3 do not all say "Frequently Asked Questions" (FRIDGE-1031). */}
            <PageSectionHeader
              eyebrow="Frequently Asked Questions"
              title={`${collection.title} FAQ`}
            />
            <FAQAccordion items={faqs} columns={2} defaultOpen={[0, 1]} bordered={false} title="" />
          </div>
        </section>
      )}

      {/* Share buttons — bottom */}
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-6">
        <ProductShareButtons
          productTitle={collection.title}
          pageType="collection"
        />
      </div>

      {/* === EXPLORE MORE — 5×2 category grid (hardcoded, user-approved; indexed-gated per plan §0.16) === */}
      <section className="bg-gray-50 py-10 border-t border-gray-100">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8">
          {/* FRIDGE-585 — lateral sibling links: the long tail was indexed but
              received almost no internal links (59 of 13,453 collections in
              Bing's page data). Server-computed token siblings, indexed-only. */}
          {siblingCollections && siblingCollections.length > 0 && (
            <div className="mb-8">
              <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-2 block uppercase">Related Collections</span>
              <CollectionSiblingLinks collections={siblingCollections} />
            </div>
          )}
          <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-2 block uppercase">Keep Exploring</span>
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-gray-900 mb-6">Browse by Category</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {visibleExploreCategories.map((cat) => (
              <CategoryCard
                key={cat.label}
                label={cat.label}
                href={cat.href}
                imageUrl={cat.imageUrl}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner removed per user request */}
    </>
  )
}
